import { useState, useEffect, useContext } from "react";
import toast from "../../utils/toast";
import { Link } from "react-router-dom";
import { blogsApi, categoriesApi, uploadApi } from "../../services";
import apiClient from "../../services/apiConfig";
import { Context } from "../../Context/Context";
import Pagination from "../../components/Pagination/Pagination";
import "./blogs.css";

const PAGE_SIZE = 9;

export default function Blogs() {
  const { user } = useContext(Context);
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [displayOnFilter, setDisplayOnFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // null | 'building' | 'success' | 'failed'

  const PF = "https://backend.hoanglecf.com";

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, categoryFilter, displayOnFilter]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      let params = {};
      if (filter !== "all") params.status = filter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (displayOnFilter !== "all") params.displayOn = displayOnFilter;
      const response = await blogsApi.getAllBlogs(params);
      setBlogs(response);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesApi.getAllCategories();
      setCategories(response);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const extractFilenameFromPath = (path) => {
    if (!path) return null;
    if (path.includes('/images/')) return path.split('/images/')[1];
    if (path.includes('/videos/')) return path.split('/videos/')[1];
    if (path.startsWith('/images/')) return path.substring(8);
    if (path.startsWith('/videos/')) return path.substring(8);
    return path;
  };

  const deleteImagesFromEditorJSContent = async (content) => {
    try {
      const data = typeof content === 'string' ? JSON.parse(content) : content;
      if (data && Array.isArray(data.blocks)) {
        for (const block of data.blocks) {
          if (block.type === 'image' && block.data) {
            const imageUrl = block.data.file?.url || block.data.url;
            if (imageUrl) {
              const filename = extractFilenameFromPath(imageUrl);
              if (filename) await uploadApi.deleteFile(filename, 'image');
            }
          }
        }
      }
    } catch (error) {
      console.error('Error deleting EditorJS images:', error);
    }
  };

  const handleDelete = async (blog) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa bài viết này?")) {
      try {
        if (blog.content) await deleteImagesFromEditorJSContent(blog.content);
        if (blog.coverImage) {
          const filename = extractFilenameFromPath(blog.coverImage);
          if (filename) await uploadApi.deleteFile(filename, 'image');
        }
        if (blog.images && blog.images.length > 0) {
          for (const image of blog.images) {
            const filename = extractFilenameFromPath(image);
            if (filename) await uploadApi.deleteFile(filename, 'image');
          }
        }
        if (blog.videos && blog.videos.length > 0) {
          for (const video of blog.videos) {
            const filename = extractFilenameFromPath(video);
            if (filename) await uploadApi.deleteFile(filename, 'video');
          }
        }
        await blogsApi.deleteBlog(blog._id, user._id);
        fetchBlogs();
        toast.success("Xóa bài viết thành công!");
      } catch (error) {
        console.error("Error deleting blog:", error);
        toast.error("Có lỗi xảy ra khi xóa bài viết!");
      }
    }
  };

  const toggleStatus = async (blog) => {
    try {
      const newStatus = blog.status === "published" ? "draft" : "published";
      await blogsApi.updateBlogStatus(blog._id, {
        authorId: user._id,
        status: newStatus
      });
      fetchBlogs();
    } catch (error) {
      console.error("Error updating blog status:", error);
      toast.error("Có lỗi xảy ra khi cập nhật trạng thái!");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "published": return { text: "Đã đăng", class: "status-published" };
      case "draft": return { text: "Bản nháp", class: "status-draft" };
      default: return { text: "Không xác định", class: "status-unknown" };
    }
  };

  const handleSyncThueacccf = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncStatus('building');
    try {
      await apiClient.post('/webhook/rebuild-thueacccf', { userId: user._id });
      // Poll status every 5s
      const poll = setInterval(async () => {
        try {
          const res = await apiClient.get('/webhook/rebuild-status');
          const data = res.data;
          if (!data.inProgress) {
            clearInterval(poll);
            setSyncing(false);
            setSyncStatus(data.lastRebuildStatus === 'success' ? 'success' : 'failed');
            setTimeout(() => setSyncStatus(null), 8000);
          }
        } catch {
          clearInterval(poll);
          setSyncing(false);
          setSyncStatus('failed');
          setTimeout(() => setSyncStatus(null), 8000);
        }
      }, 5000);
    } catch (err) {
      setSyncing(false);
      const msg = err.response?.data?.error || 'Lỗi không xác định';
      if (err.response?.status === 429) {
        setSyncStatus('building');
      } else {
        setSyncStatus('failed');
        toast.error(msg);
        setTimeout(() => setSyncStatus(null), 5000);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getExcerptFromContent = (content, maxLen = 150) => {
    if (!content) return "";
    try {
      const data = typeof content === 'string' ? JSON.parse(content) : content;
      if (data && Array.isArray(data.blocks)) {
        const firstTextBlock = data.blocks.find(b => {
          if (!b) return false;
          const text = (b.data && (b.data.text || b.data.caption || "")) || "";
          return typeof text === 'string' && text.replace(/<[^>]+>/g, '').trim().length > 0;
        });
        const raw = firstTextBlock ? (firstTextBlock.data.text || firstTextBlock.data.caption || "") : "";
        const plain = raw.replace(/<[^>]+>/g, '').trim();
        return plain.length > maxLen ? plain.substring(0, maxLen) + "..." : plain;
      }
    } catch (_) {}
    const plain = String(content).replace(/<[^>]+>/g, '').trim();
    return plain.length > maxLen ? plain.substring(0, maxLen) + "..." : plain;
  };

  const hasActiveFilters = filter !== "all" || categoryFilter !== "all" || displayOnFilter !== "all" || searchTerm.trim() !== "";

  // Filter by title (client-side); status/category are filtered server-side
  const filteredBlogs = blogs.filter((blog) =>
    (blog.title || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset to first page whenever the result set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [blogs, searchTerm]);

  const totalPages = Math.ceil(filteredBlogs.length / PAGE_SIZE);
  const pagedBlogs = filteredBlogs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  if (loading) {
    return (
      <div className="blogs-page">
        <div className="blogs-container">
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Đang tải...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blogs-page">
      <div className="blogs-container">
        <div className="page-header">
          {/* Row 1 — title + primary action */}
          <div className="page-header-top">
            <div className="page-title">
              <h1>Quản lý bài viết</h1>
              <p>Quản lý và theo dõi các bài viết</p>
            </div>
            <div className="toolbar-actions">
              <button
                className={`btn btn-sync ${syncing ? 'syncing' : ''} ${syncStatus === 'success' ? 'sync-ok' : ''} ${syncStatus === 'failed' ? 'sync-fail' : ''}`}
                onClick={handleSyncThueacccf}
                disabled={syncing}
                title="Sync & rebuild thueacccf.net"
              >
                <i className={`fas ${syncing ? 'fa-spinner fa-spin' : syncStatus === 'success' ? 'fa-check' : syncStatus === 'failed' ? 'fa-times' : 'fa-sync-alt'}`}></i>
                <span>{syncing ? 'Đang build...' : syncStatus === 'success' ? 'Sync OK!' : syncStatus === 'failed' ? 'Thất bại' : 'Sync thueacccf'}</span>
              </button>
              <Link to="/blogs/create" className="btn btn-primary">
                <i className="fas fa-plus"></i>
                <span>Tạo bài viết</span>
              </Link>
            </div>
          </div>

          {/* Row 2 — search + filters */}
          <div className="page-toolbar">
            <div className="toolbar-search">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Tìm kiếm theo tiêu đề bài viết..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="toolbar-filter">
              <span className="filter-bar-label">Lọc:</span>
              <select
                className="filter-select"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">Tất cả trạng thái</option>
                <option value="published">Đã đăng</option>
                <option value="draft">Bản nháp</option>
              </select>
              <select
                className="filter-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Tất cả danh mục</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
              <select
                className="filter-select"
                value={displayOnFilter}
                onChange={(e) => setDisplayOnFilter(e.target.value)}
              >
                <option value="all">Tất cả trang hiển thị</option>
                <option value="hoanglecf">hoanglecf.com</option>
                <option value="hoanglecf-vn">hoanglecf.vn</option>
                <option value="thueacccf">thueacccf.net</option>
              </select>
              {hasActiveFilters && (
                <button
                  className="filter-reset-btn"
                  onClick={() => { setFilter("all"); setCategoryFilter("all"); setDisplayOnFilter("all"); setSearchTerm(""); }}
                >
                  <i className="fas fa-times"></i> Xóa bộ lọc
                </button>
              )}
              <span className="filter-summary-inline">
                Hiển thị <strong>{filteredBlogs.length}</strong> bài viết
              </span>
            </div>
          </div>
        </div>

        <div className="blogs-content">
          {blogs.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-blog"></i>
              <h3>Chưa có bài viết nào</h3>
              <p>Hãy tạo bài viết đầu tiên của bạn</p>
              <Link to="/blogs/create" className="btn btn-primary">
                <i className="fas fa-plus"></i>
                <span>Tạo bài viết đầu tiên</span>
              </Link>
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-search"></i>
              <h3>Không tìm thấy bài viết nào</h3>
              <p>
                {searchTerm
                  ? `Không có bài viết nào phù hợp với từ khóa "${searchTerm}"`
                  : "Không có bài viết nào phù hợp với bộ lọc"}
              </p>
            </div>
          ) : (
            <div className="blogs-grid">
              {pagedBlogs.map((blog) => {
                const statusBadge = getStatusBadge(blog.status);
                return (
                  <div key={blog._id} className="blog-card">
                    <div className="blog-image">
                      {blog.coverImage ? (
                        <img src={PF + blog.coverImage} alt={blog.title || "cover"} />
                      ) : (
                        <div className="no-image">
                          <i className="fas fa-image"></i>
                        </div>
                      )}
                      <div className={`blog-status ${statusBadge.class}`}>
                        {statusBadge.text}
                      </div>
                    </div>
                    <div className="blog-content">
                      <div className="blog-meta-tags">
                        {blog.displayOn && blog.displayOn.length > 0 && (
                          <div className="blog-display-on">
                            {blog.displayOn.map(site => (
                              <span key={site} className={`display-badge display-badge--${site}`}>
                                <i className="fas fa-globe"></i> {site === 'hoanglecf' ? 'hoanglecf.com' : site === 'hoanglecf-vn' ? 'hoanglecf.vn' : 'thueacccf.net'}
                              </span>
                            ))}
                          </div>
                        )}
                        <span
                          className="blog-category"
                          style={{
                            '--category-color': blog.category?.color || '#667eea',
                          }}
                        >
                          <i className={blog.category?.icon || 'fas fa-tag'}></i>
                          {blog.category?.name || "Không có danh mục"}
                        </span>
                      </div>

                      <h3 className="blog-title" title={blog.title || "(Chưa có tiêu đề)"}>
                        {blog.title || "(Chưa có tiêu đề)"}
                      </h3>
                      <div className="blog-excerpt">{getExcerptFromContent(blog.content)}</div>

                      <div className="blog-meta-info">
                        <span className="blog-author" title="Tác giả">
                          <i className="fas fa-user"></i> {blog.authorId?.username || "Admin"}
                        </span>
                        <span className="blog-date" title="Ngày tạo">
                          <i className="fas fa-calendar-alt"></i> {formatDate(blog.createdAt)}
                        </span>
                      </div>

                      {blog.slug && (
                        <div 
                          className="blog-slug-bar" 
                          title="Click để copy link" 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            const catName = blog.category?.name?.toLowerCase() || '';
                            const isHotNews = catName.includes('hotnews') || catName.includes('hóng biến') || catName.includes('hot news');
                            const folder = isHotNews ? 'hong-bien' : 'cong-dong';
                            navigator.clipboard.writeText(`https://hoanglecf.com/${folder}/${blog.slug}`); 
                            toast.info('Đã copy link bài viết!'); 
                          }}
                        >
                          <i className="fas fa-link"></i>
                          <span className="slug-text">/{blog.slug}</span>
                          <i className="fas fa-copy copy-icon"></i>
                        </div>
                      )}

                      <div className="blog-stats-row">
                        <span className="stat-item" title="Lượt xem">
                          <i className="fas fa-eye"></i> {blog.viewCount || 0}
                        </span>
                        {blog.readingTime > 0 && (
                          <span className="stat-item" title="Thời gian đọc">
                            <i className="fas fa-clock"></i> {blog.readingTime}m
                          </span>
                        )}
                        {blog.images && blog.images.length > 0 && (
                          <span className="stat-item" title="Hình ảnh">
                            <i className="fas fa-images"></i> {blog.images.length}
                          </span>
                        )}
                        {blog.videos && blog.videos.length > 0 && (
                          <span className="stat-item" title="Video">
                            <i className="fas fa-video"></i> {blog.videos.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="blog-actions">
                      <button
                        className="action-btn status-btn"
                        onClick={() => toggleStatus(blog)}
                        title={blog.status === "published" ? "Chuyển thành bản nháp" : "Xuất bản"}
                      >
                        <i className={`fas ${blog.status === "published" ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </button>
                      <Link
                        to={`/blogs/edit/${blog._id}`}
                        className="action-btn edit-btn"
                        title="Chỉnh sửa"
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(blog)}
                        title="Xóa"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
}
